/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Smart link-preview tile. The sender unfurls a shared URL once
// (og:title / description / image) and ships the scraped metadata E2E,
// so the receiver renders this card without ever fetching the URL. One
// card serves both bubble and compact modes — `MessageRow` already
// constrains width + alignment, so the card stays left-anchored and
// full-bleed in either layout.
//
// Layout: an optional 16:9 image banner, then a body with the title
// (2 lines, bold), description (2 lines, secondary) and the bare domain.
// The whole card is a Pressable → `onOpen` (host Linking.openURL).

import { Image, Pressable, Text, View } from 'react-native';
import { Link2 } from 'lucide-react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import { useTileMaxWidth } from '../tileConstraint';

export interface LinkPreviewCardProps {
  readonly url: string;
  readonly title: string;
  readonly description?: string | undefined;
  readonly imageUri?: string | undefined;
  readonly domain?: string | undefined;
  readonly isSelf?: boolean | undefined;
  readonly onOpen: () => void;
  readonly testID?: string | undefined;
}

export function LinkPreviewCard({
  url,
  title,
  description,
  imageUri,
  domain,
  isSelf,
  onOpen,
  testID,
}: LinkPreviewCardProps): JSX.Element {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];
  // Thread side panel: cap the tile to the provided width.
  const tileMax = useTileMaxWidth();

  const wrapBackground = isSelf ? colors.accent.brand : colors.surface.elevated;
  const titleColor = isSelf ? colors.text.onAccent : colors.text.primary;
  const subColor = isSelf ? 'rgba(255,255,255,0.7)' : colors.text.secondary;
  const domainLabel = domain ?? safeDomain(url);
  // When the unfurl produced no real og:title it falls back to the bare
  // host — which would then render IDENTICALLY in the domain row and the
  // title row ("docs.maptiler.com" twice). In that case show the cleaned
  // full URL (host + path, no scheme) as the title so the card carries
  // useful info instead of a duplicate.
  const titleIsJustDomain =
    domainLabel !== undefined && title.trim().toLowerCase() === domainLabel.toLowerCase();
  const displayTitle = titleIsJustDomain
    ? url.replace(/^https?:\/\//i, '').replace(/\/$/, '')
    : title;

  return (
    <Pressable
      onPress={onOpen}
      accessibilityRole="link"
      accessibilityLabel={`Open link: ${title}`}
      className="rounded-[16px] overflow-hidden"
      style={{
        width: 300,
        maxWidth: tileMax ?? '100%',
        backgroundColor: wrapBackground,
      }}
      testID={testID}
    >
      {imageUri !== undefined ? (
        <View className="w-full overflow-hidden" style={{ aspectRatio: 1.91 }}>
          <Image
            source={{ uri: imageUri }}
            resizeMode="cover"
            accessibilityIgnoresInvertColors
            style={{ width: '100%', height: '100%' }}
          />
        </View>
      ) : null}
      <View className="px-md py-sm gap-1">
        {domainLabel !== undefined ? (
          <View className="flex flex-row items-center gap-1">
            <Link2 size={11} color={subColor} />
            <Text
              numberOfLines={1}
              className="font-sans text-[11px] leading-[14px] uppercase"
              style={{ color: subColor, letterSpacing: 0.3 }}
            >
              {domainLabel}
            </Text>
          </View>
        ) : null}
        <Text
          numberOfLines={2}
          className="font-sans text-[14px] leading-[19px] font-bold"
          style={{ color: titleColor }}
        >
          {displayTitle}
        </Text>
        {description !== undefined && description.length > 0 ? (
          <Text
            numberOfLines={2}
            className="font-sans text-[12px] leading-[16px]"
            style={{ color: subColor }}
          >
            {description}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

function safeDomain(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}
